/**
 * Agent Skills tab — Sprint 131 Stream C.
 *
 * Combines:
 *   - Scope mode selector (none / all / whitelist / filter)
 *   - Scope filter builder (verticals, functions, categories, tags)
 *   - Live preview (debounced 500 ms, calls POST /preview)
 *   - Per-binding overrides table (mode, parameters, triggers, priority)
 *
 * For new agents (no agentId yet) the tab shows a hint to save first.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, Badge, Button, Spinner } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkles, Eye, Trash2, Save, GitFork } from 'lucide-react';
import {
  useAgentSkillScope,
  useUpdateAgentSkillScope,
  useAgentScopePreview,
  useAgentSkillBindings,
  useUpsertAgentSkillBinding,
  usePatchAgentSkillBinding,
  useDeleteAgentSkillBinding,
  useTenantSkillCatalog,
  useAdminSkillFacets,
  useHiveInheritancePreview,
} from '@/features/skills';
import type {
  ActivationMode,
  ScopeMode,
  SkillScope,
  SkillFilterDSL,
  SkillScopePreviewMatch,
  AgentSkillBinding,
} from '@/features/skills';
import { useI18n } from '@/i18n';
import { UnifiedSkillSearchInput } from './UnifiedSkillSearchInput';
import { parseUnifiedSkillSearch } from '@/features/skills/utils/unifiedSearch';
import { getErrorMessage } from '@/lib/api-client';

interface Props {
  agentId: string | undefined;
  /** Hive del agente (si pertenece a uno) — activa la sección de herencia
   *  de skills (ISS-563: decisión de producto 12-Oct-2026). */
  hiveId?: string | null;
  /** En agentes `use_only` (enmienda 14-Oct-2026) el `skills_scope` es
   *  definición del publisher: se oculta el selector de scope + preview y
   *  solo se gestionan bindings propios. */
  hideScope?: boolean;
}

const DEFAULT_FILTER: SkillFilterDSL = {
  verticals: [],
  functions: [],
  categories: [],
  tags_any: [],
  tags_all: [],
  exclude_tags: [],
  lifecycle: ['published'],
  min_version: undefined,
  max_version: undefined,
};

/**
 * Normalize a scope for comparison (saved vs local). The backend returns
 * defaulted fields (always_include_ids, null versions…) that the local form
 * doesn't send, so a raw JSON comparison would never stabilize.
 */
function normalizeScopeForCompare(scope: SkillScope | null | undefined): string {
  if (!scope) return 'mode:none';
  switch (scope.mode) {
    case 'none':
      return 'mode:none';
    case 'all':
      return 'mode:all';
    case 'whitelist':
      return `mode:whitelist:${[...(scope.whitelist_ids ?? [])].sort().join(',')}`;
    case 'filter': {
      const f = scope.filter ?? {};
      return JSON.stringify({
        verticals: f.verticals ?? [],
        functions: f.functions ?? [],
        categories: f.categories ?? [],
        tags_any: f.tags_any ?? [],
        tags_all: f.tags_all ?? [],
        exclude_tags: f.exclude_tags ?? [],
        lifecycle: f.lifecycle ?? [],
        min_version: f.min_version ?? null,
        max_version: f.max_version ?? null,
        max_inject_tokens_per_skill: f.max_inject_tokens_per_skill ?? null,
      });
    }
  }
}

const ACTIVATION_OPTIONS: ActivationMode[] = [
  'auto',
  'always',
  'manual',
  'triggered',
];

function csvToList(s: string): string[] {
  return s
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

function listToCsv(l?: string[] | null): string {
  return (l ?? []).join(', ');
}

export function AgentSkillsTab({ agentId, hiveId, hideScope }: Props) {
  if (!agentId) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground text-sm">
          Save the agent first to configure its Skills scope and bindings.
        </p>
      </Card>
    );
  }
  return <AgentSkillsTabBody agentId={agentId} hiveId={hiveId} hideScope={hideScope} />;
}

function AgentSkillsTabBody({
  agentId,
  hiveId,
  hideScope,
}: {
  agentId: string;
  hiveId?: string | null;
  hideScope?: boolean;
}) {
  const { t } = useI18n();
  const scopeQuery = useAgentSkillScope(agentId);
  const updateScope = useUpdateAgentSkillScope(agentId);
  const previewMutation = useAgentScopePreview(agentId);
  const bindings = useAgentSkillBindings(agentId);
  const upsertBinding = useUpsertAgentSkillBinding(agentId);
  // ISS-563 (decisión 12-Oct-2026): cuando el agente pertenece a un hive se
  // muestra la herencia — las bindings del hive aplican SIEMPRE en runtime,
  // incluso con scope del agente `none`.
  const inheritanceQuery = useHiveInheritancePreview(hiveId ?? undefined, agentId);

  // Local editable copy of scope for the form.
  // Sprint 365 C (CHG-SKL-001): un agente nuevo nace con `all`; si la API
  // devuelve un scope persistido, la hidratación lo sustituye.
  const [mode, setMode] = useState<ScopeMode>('all');
  const [whitelistIds, setWhitelistIds] = useState<string[]>([]);
  const [filter, setFilter] = useState<SkillFilterDSL>(DEFAULT_FILTER);
  const [maxInjectTokens, setMaxInjectTokens] = useState<string>('');

  // Hydrate when scope loads — only ONCE. Later refetches (e.g. after
  // autosave) must not clobber in-flight edits.
  const scopeHydratedRef = useRef(false);
  useEffect(() => {
    const s = scopeQuery.data;
    if (!s || scopeHydratedRef.current) return;
    scopeHydratedRef.current = true;
    setMode(s.mode);
    if (s.mode === 'whitelist') setWhitelistIds(s.whitelist_ids ?? []);
    if (s.mode === 'filter') {
      setFilter({ ...DEFAULT_FILTER, ...s.filter });
      setMaxInjectTokens(
        s.filter.max_inject_tokens_per_skill !== undefined &&
          s.filter.max_inject_tokens_per_skill !== null
          ? String(s.filter.max_inject_tokens_per_skill)
          : '',
      );
    } else {
      setMaxInjectTokens('');
    }
  }, [scopeQuery.data]);

  // Build the SkillScope payload from local state.
  const builtScope: SkillScope = useMemo(() => {
    const mit = maxInjectTokens.trim() ? Number(maxInjectTokens) : undefined;
    if (mode === 'none') return { mode: 'none' };
    if (mode === 'all') return { mode: 'all' };
    if (mode === 'whitelist') {
      return {
        mode: 'whitelist',
        whitelist_ids: whitelistIds,
      };
    }
    return {
      mode: 'filter',
      filter: { ...filter, max_inject_tokens_per_skill: mit ?? null },
    };
  }, [mode, whitelistIds, filter, maxInjectTokens]);

  // Whitelist with no selection is not a valid scope yet (backend requires at
  // least one id) — used to skip the preview and to guide the user.
  const isWhitelistEmpty = mode === 'whitelist' && whitelistIds.length === 0;

  // Debounced AUTOSAVE — any valid scope change (mode switch, whitelist
  // add/remove, filter edit) is persisted without needing the Save button.
  const savedScopeSig = useMemo(
    () => normalizeScopeForCompare(scopeQuery.data ?? null),
    [scopeQuery.data],
  );
  const builtScopeSig = useMemo(
    () => normalizeScopeForCompare(builtScope),
    [builtScope],
  );
  useEffect(() => {
    if (!scopeHydratedRef.current || isWhitelistEmpty) return;
    if (builtScopeSig === savedScopeSig) return;
    const handle = window.setTimeout(async () => {
      try {
        await updateScope.mutateAsync({ scope: builtScope });
      } catch (err) {
        toast.error(getErrorMessage(err));
      }
    }, 600);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builtScopeSig, savedScopeSig, isWhitelistEmpty]);

  // Debounced preview.
  const [preview, setPreview] = useState<{
    matched_count: number;
    matches: SkillScopePreviewMatch[];
  } | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  useEffect(() => {
    const handle = window.setTimeout(async () => {
      if (!scopeHydratedRef.current) {
        setPreview(null);
        setPreviewBusy(false);
        return;
      }
      if (isWhitelistEmpty) {
        setPreview(null);
        setPreviewBusy(false);
        return;
      }
      try {
        setPreviewBusy(true);
        const res = await previewMutation.mutateAsync({ scope: builtScope });
        setPreview({ matched_count: res.matched_count, matches: res.matched_skills });
      } catch {
        // ignored — invalid payloads return errors that the user can see in the
        // matched count area.
        setPreview(null);
      } finally {
        setPreviewBusy(false);
      }
    }, 500);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builtScope]);

  async function handleSaveScope() {
    try {
      await updateScope.mutateAsync({ scope: builtScope });
      toast.success(
        t('admin.skills.agentSkillsTab.scopeSaved') || 'Skills scope saved'
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <div className="space-y-4">
      {/* En use_only el scope es definición del publisher (oculto); solo bindings propios. */}
      {!hideScope && (
      <>
      {/* Mode selector */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">
            {t('admin.skills.agentSkillsTab.scopeTitle') || 'Skills scope'}
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {t('admin.skills.agentSkillsTab.scopeDescription') ||
            'Choose which catalog skills are eligible for this agent. The selection is evaluated at runtime — published skills are added/removed automatically.'}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          {(['none', 'all', 'whitelist', 'filter'] as ScopeMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded-md border p-3 text-left text-sm transition ${
                mode === m
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="font-semibold">
                {t(`admin.skills.agentSkillsTab.modeName${m.charAt(0).toUpperCase()}${m.slice(1)}`) ||
                  m}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {m === 'none' &&
                  (t('admin.skills.agentSkillsTab.modeNone') || 'No skills.')}
                {m === 'all' &&
                  (t('admin.skills.agentSkillsTab.modeAll') || 'All published skills.')}
                {m === 'whitelist' &&
                  (t('admin.skills.agentSkillsTab.modeWhitelist') ||
                    'Explicit skill list.')}
                {m === 'filter' &&
                  (t('admin.skills.agentSkillsTab.modeFilter') ||
                    'Match by classification/tags.')}
              </div>
            </button>
          ))}
        </div>

        <p className="text-xs text-muted-foreground mb-4" data-testid="skills-scope-effective">
          {t('admin.skills.agentSkillsTab.effectiveMode') || 'Effective mode'}:{' '}
          <strong>
            {t(`admin.skills.agentSkillsTab.modeName${mode.charAt(0).toUpperCase()}${mode.slice(1)}`) ||
              mode}
          </strong>
          {' · '}
          {t('admin.skills.agentSkillsTab.effectiveOrigin') || 'Source: agent configuration'}
        </p>

        {mode === 'whitelist' && (
          <WhitelistSkillPicker
            selectedIds={whitelistIds}
            onAdd={(id) => setWhitelistIds((prev) => [...prev, id])}
            onRemove={(id) =>
              setWhitelistIds((prev) => prev.filter((x) => x !== id))
            }
          />
        )}

        {mode === 'filter' && (
          <FilterBuilder filter={filter} onChange={setFilter} />
        )}

        {mode === 'filter' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div className="space-y-1">
              <Label>
                {t('admin.skills.agentSkillsTab.maxInjectTokens') ||
                  'Max inject tokens per skill'}
              </Label>
              <Input
                value={maxInjectTokens}
                onChange={(e) => setMaxInjectTokens(e.target.value)}
                placeholder="e.g. 4000"
                type="number"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button
            onClick={handleSaveScope}
            disabled={updateScope.isPending || isWhitelistEmpty}
          >
            <Save className="h-4 w-4 mr-2" />
            {updateScope.isPending
              ? t('admin.skills.agentSkillsTab.saving') || 'Saving…'
              : t('admin.skills.agentSkillsTab.saveScope') || 'Save scope'}
          </Button>
        </div>
      </Card>
      </>
      )}

      {/* ISS-563 — Skills heredadas del hive (decisión 12-Oct-2026): los
          agentes pertenecientes a un hive muestran la herencia y se indica
          que es heredado. Semántica: las bindings del hive aplican SIEMPRE
          en runtime, incluso con scope del agente 'none'. */}
      {hiveId && (
        <Card className="p-4 border-violet-200 dark:border-violet-900/40">
          <div className="flex items-center gap-2 mb-3">
            <GitFork className="h-5 w-5 text-violet-500" />
            <h3 className="font-semibold">
              {t('admin.skills.agentSkillsTab.hiveInheritedTitle') ||
                'Skills inherited from the hive'}
            </h3>
            {inheritanceQuery.isFetching && <Spinner size="sm" />}
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            {t('admin.skills.agentSkillsTab.hiveInheritedDescription') ||
              'This agent belongs to a hive. Hive-bound skills always apply at runtime, even when the agent scope is "none".'}
          </p>
          {inheritanceQuery.data && inheritanceQuery.data.items.length > 0 ? (
            <div className="space-y-1">
              {inheritanceQuery.data.items.map((item) => (
                <div
                  key={item.skill_id}
                  className="flex items-center gap-2 text-sm border-b last:border-0 py-1"
                >
                  <Badge variant="outline" className="font-mono text-xs">
                    {item.slug}
                  </Badge>
                  <span className="flex-1 truncate">{item.name ?? item.slug}</span>
                  <Badge
                    variant={
                      item.source === 'hive'
                        ? 'default'
                        : item.source === 'agent_overrides_hive'
                          ? 'secondary'
                          : 'outline'
                    }
                    className="text-xs"
                  >
                    {item.source === 'hive' &&
                      (t('admin.skills.agentSkillsTab.sourceHive') || 'Inherited from hive')}
                    {item.source === 'agent' &&
                      (t('admin.skills.agentSkillsTab.sourceAgent') || 'Agent binding')}
                    {item.source === 'agent_overrides_hive' &&
                      (t('admin.skills.agentSkillsTab.sourceOverride') || 'Agent overrides hive')}
                  </Badge>
                </div>
              ))}
            </div>
          ) : inheritanceQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">
              {t('admin.skills.agentSkillsTab.loading') || 'Loading…'}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('admin.skills.agentSkillsTab.noInheritedSkills') ||
                'The hive has no bound skills.'}
            </p>
          )}
        </Card>
      )}

      {/* Preview */}
      {!hideScope && (
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Eye className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">
            {t('admin.skills.agentSkillsTab.livePreviewTitle') ||
              'Live preview of skills'}
          </h3>
          {previewBusy && <Spinner size="sm" />}
        </div>
        {preview ? (
          <>
            <p className="text-sm mb-3">
              <strong>{preview.matched_count}</strong>{' '}
              {t('admin.skills.agentSkillsTab.matchedSkills') ||
                'skill(s) currently match this scope.'}
            </p>
            {preview.matches.length > 0 && (
              <div className="space-y-1">
                {preview.matches.slice(0, 5).map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center gap-2 text-sm border-b last:border-0 py-1"
                  >
                    <Badge variant="outline" className="font-mono text-xs">
                      {m.slug}
                    </Badge>
                    <span className="flex-1 truncate">{m.name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {m.tier}
                    </Badge>
                  </div>
                ))}
                {preview.matched_count > 5 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    +
                    {preview.matched_count - 5}{' '}
                    {t('admin.skills.agentSkillsTab.moreSkills') || 'more…'}
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            {isWhitelistEmpty
              ? t('admin.skills.agentSkillsTab.whitelistEmptyHint') ||
                'Select at least one skill to preview the whitelist.'
              : t('admin.skills.agentSkillsTab.adjustScope') ||
                'Adjust the scope to preview skills.'}
          </p>
        )}
      </Card>
      )}

      {/* Bindings */}
      <BindingsTable
        agentId={agentId}
        bindings={bindings.data ?? []}
        loading={bindings.isLoading}
        onAdd={async (skillId) => {
          try {
            // New bindings default to 'auto' (API default) — the auto-router
            // selects the skill per task; router failure falls back to
            // activating all AUTO candidates (ISS-312).
            await upsertBinding.mutateAsync({ skill_id: skillId });
            toast.success(
              t('admin.skills.agentSkillsTab.bindingAdded') || 'Binding added'
            );
          } catch (err) {
            toast.error(getErrorMessage(err));
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Whitelist picker — visual skill list with dynamic name/description search
// and pagination over the full published catalog when the query is empty.
// ---------------------------------------------------------------------------

interface WhitelistSkillPickerProps {
  selectedIds: string[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
}

const WHITELIST_PAGE_SIZE = 25;

function WhitelistSkillPicker({
  selectedIds,
  onAdd,
  onRemove,
}: WhitelistSkillPickerProps) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);

  const searching = query.trim().length > 0;
  const results = useTenantSkillCatalog({
    search: searching ? query.trim() : undefined,
    lifecycle: 'published',
    page_size: WHITELIST_PAGE_SIZE,
    page: searching ? 1 : page,
  });

  // Full published catalog (once) to render names for already-selected ids.
  const catalog = useTenantSkillCatalog({ lifecycle: 'published', page_size: 100 });
  const nameById = useMemo(() => {
    const arr = !catalog.data
      ? []
      : Array.isArray(catalog.data)
        ? catalog.data
        : catalog.data.data ?? [];
    const map: Record<string, string> = {};
    for (const s of arr) map[s.id] = s.display_name;
    return map;
  }, [catalog.data]);

  const candidates = useMemo(() => {
    const arr = !results.data
      ? []
      : Array.isArray(results.data)
        ? results.data
        : results.data.data ?? [];
    return arr.filter((s) => !selectedIds.includes(s.id));
  }, [results.data, selectedIds]);

  const total = !results.data
    ? 0
    : Array.isArray(results.data)
      ? 0
      : results.data.total ?? 0;
  const hasMore = !searching && page * WHITELIST_PAGE_SIZE < total;

  return (
    <div className="space-y-3">
      <div className="relative">
        <Label>
          {t('admin.skills.agentSkillsTab.addSkillsLabel') ||
            'Add skills to the whitelist'}
        </Label>
        <Input
          className="mt-1"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          placeholder={
            t('admin.skills.agentSkillsTab.searchSkillsPlaceholder') ||
            'Search skills by name or description…'
          }
        />

        {open && (
          <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md max-h-72 overflow-auto">
            {results.isLoading ? (
              <div className="flex justify-center py-4">
                <Spinner size="sm" />
              </div>
            ) : candidates.length === 0 ? (
              <p className="text-sm text-muted-foreground py-3 px-3 text-center">
                {t('admin.skills.agentSkillsTab.noMatchingSkills') ||
                  'No matching skills.'}
              </p>
            ) : (
              <>
                <div className="py-1">
                  {candidates.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-accent"
                    >
                      <Badge variant="outline" className="font-mono text-xs shrink-0">
                        {s.slug}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <span className="block truncate text-sm font-medium">
                          {s.display_name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {s.description}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {s.vertical}
                      </Badge>
                      <Button
                        size="sm"
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          onAdd(s.id);
                          setQuery('');
                          setPage(1);
                        }}
                      >
                        {t('admin.skills.agentSkillsTab.addButton') || 'Add'}
                      </Button>
                    </div>
                  ))}
                </div>
                {hasMore && (
                  <div className="border-t px-3 py-2 text-center">
                    <Button
                      size="sm"
                      variant="ghost"
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      {t('admin.skills.agentSkillsTab.showMore') || 'Show more'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="space-y-1">
          <Label>
            {t('admin.skills.agentSkillsTab.whitelistedSkills') ||
              'Whitelisted skills'}
          </Label>
          <div className="flex flex-wrap gap-2">
            {selectedIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-3 py-1 text-sm"
              >
                <span className="font-medium">
                  {nameById[id] ??
                    (t('admin.skills.agentSkillsTab.unknownSkill') ||
                      'Unknown skill')}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${nameById[id] ?? id}`}
                  onClick={() => onRemove(id)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter builder (compact CSV editors per array; sufficient for MVP).
// ---------------------------------------------------------------------------

interface FilterBuilderProps {
  filter: SkillFilterDSL;
  onChange: (f: SkillFilterDSL) => void;
}

function FilterBuilder({ filter, onChange }: FilterBuilderProps) {
  const { t } = useI18n();
  function set<K extends keyof SkillFilterDSL>(key: K, value: SkillFilterDSL[K]) {
    onChange({ ...filter, [key]: value });
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <CsvField
        label={t('admin.skills.agentSkillsTab.filterVerticals') || 'Verticals (any)'}
        value={listToCsv(filter.verticals)}
        onChange={(v) => set('verticals', csvToList(v))}
      />
      <CsvField
        label={t('admin.skills.agentSkillsTab.filterFunctions') || 'Functions (any)'}
        value={listToCsv(filter.functions)}
        onChange={(v) => set('functions', csvToList(v))}
      />
      <CsvField
        label={t('admin.skills.agentSkillsTab.filterCategories') || 'Categories (any)'}
        value={listToCsv(filter.categories)}
        onChange={(v) => set('categories', csvToList(v))}
      />
      <CsvField
        label={t('admin.skills.agentSkillsTab.filterLifecycle') || 'Lifecycle (any)'}
        value={listToCsv(filter.lifecycle)}
        onChange={(v) => set('lifecycle', csvToList(v) as SkillFilterDSL['lifecycle'])}
      />
      <CsvField
        label={t('admin.skills.agentSkillsTab.filterTagsAny') || 'Tags — any of'}
        value={listToCsv(filter.tags_any)}
        onChange={(v) => set('tags_any', csvToList(v))}
      />
      <CsvField
        label={t('admin.skills.agentSkillsTab.filterTagsAll') || 'Tags — all of'}
        value={listToCsv(filter.tags_all)}
        onChange={(v) => set('tags_all', csvToList(v))}
      />
      <CsvField
        label={t('admin.skills.agentSkillsTab.filterExcludeTags') || 'Exclude tags'}
        value={listToCsv(filter.exclude_tags)}
        onChange={(v) => set('exclude_tags', csvToList(v))}
      />
    </div>
  );
}

function CsvField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          t('admin.skills.agentSkillsTab.csvPlaceholder') || 'comma-separated'
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bindings table with inline edit + add picker.
// ---------------------------------------------------------------------------

interface BindingsTableProps {
  agentId: string;
  bindings: AgentSkillBinding[];
  loading: boolean;
  onAdd: (skillId: string) => Promise<void> | void;
}

function BindingsTable({ agentId, bindings, loading, onAdd }: BindingsTableProps) {
  const { t } = useI18n();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Tenant catalog (all lifecycles) to resolve display names for bound skills.
  const catalog = useTenantSkillCatalog({ page_size: 200 });
  const nameById = useMemo(() => {
    const arr = !catalog.data
      ? []
      : Array.isArray(catalog.data)
        ? catalog.data
        : catalog.data.data ?? [];
    const map: Record<string, string> = {};
    for (const s of arr) map[s.id] = s.display_name ?? s.slug ?? s.id;
    return map;
  }, [catalog.data]);

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">
          {t('admin.skills.agentSkillsTab.bindingsTitle') ||
            'Per-skill overrides'}
        </h3>
        <Button size="sm" onClick={() => setPickerOpen(true)}>
          {t('admin.skills.agentSkillsTab.addBinding') || 'Add binding'}
        </Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Spinner />
        </div>
      ) : bindings.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          {t('admin.skills.agentSkillsTab.noOverrides') ||
            'No overrides — every skill in scope uses its catalog defaults.'}
        </p>
      ) : (
        <div className="space-y-2">
          {bindings.map((b) => (
            <BindingRow
              key={b.id ?? b.skill_id}
              agentId={agentId}
              binding={b}
              name={nameById[b.skill_id]}
            />
          ))}
        </div>
      )}

      {pickerOpen && (
        <SkillPickerInline
          excludeIds={bindings.map((b) => b.skill_id)}
          onPick={async (id) => {
            await onAdd(id);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </Card>
  );
}

function BindingRow({
  agentId,
  binding,
  name,
}: {
  agentId: string;
  binding: AgentSkillBinding;
  name?: string;
}) {
  const patch = usePatchAgentSkillBinding(agentId);
  const del = useDeleteAgentSkillBinding(agentId);
  const { t } = useI18n();
  const [mode, setMode] = useState<ActivationMode>(binding.mode);
  const [priority, setPriority] = useState<string>(
    binding.priority !== undefined && binding.priority !== null
      ? String(binding.priority)
      : '',
  );
  const [triggersCsv, setTriggersCsv] = useState(
    listToCsv(binding.triggers ?? []),
  );

  async function save() {
    try {
      await patch.mutateAsync({
        skillId: binding.skill_id,
        payload: {
          mode,
          priority: priority.trim() ? Number(priority) : null,
          triggers: csvToList(triggersCsv),
        },
      });
      toast.success(
        t('admin.skills.agentSkillsTab.bindingSaved') || 'Binding saved'
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function remove() {
    if (
      !confirm(
        t('admin.skills.agentSkillsTab.removeBindingConfirm') ||
          'Remove this binding?'
      )
    )
      return;
    try {
      await del.mutateAsync(binding.skill_id);
      toast.success(
        t('admin.skills.agentSkillsTab.bindingRemoved') || 'Binding removed'
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <div className="border rounded-md p-3 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
      <div className="md:col-span-1">
        <Label className="text-xs">
          {t('admin.skills.agentSkillsTab.skillLabel') || 'Skill'}
        </Label>
        <span
          className="block text-xs truncate font-medium"
          title={name ? binding.skill_id : undefined}
        >
          {name ?? binding.skill_id}
        </span>
      </div>
      <div className="md:col-span-1">
        <Label className="text-xs">
          {t('admin.skills.agentSkillsTab.modeLabel') || 'Mode'}
        </Label>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as ActivationMode)}
          className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
        >
          {ACTIVATION_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {t(
                `admin.skills.agentSkillsTab.activation${m
                  .charAt(0)
                  .toUpperCase()}${m.slice(1)}`
              ) || m}
            </option>
          ))}
        </select>
      </div>
      <div className="md:col-span-1">
        <Label className="text-xs">
          {t('admin.skills.agentSkillsTab.priorityLabel') || 'Priority'}
        </Label>
        <Input
          type="number"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        />
      </div>
      <div className="md:col-span-1">
        <Label className="text-xs">
          {t('admin.skills.agentSkillsTab.triggersLabel') || 'Triggers (CSV)'}
        </Label>
        <Input
          value={triggersCsv}
          onChange={(e) => setTriggersCsv(e.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={patch.isPending}>
          {t('admin.skills.agentSkillsTab.saveButton') || 'Save'}
        </Button>
        <Button size="sm" variant="ghost" onClick={remove}>
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
}

// Inline skill picker — lists admin skills (lifecycle=published) and lets the
// user pick one. For MVP we don't filter by resolved scope; the user can pick
// any skill they have access to.
function SkillPickerInline({
  excludeIds,
  onPick,
  onClose,
}: {
  excludeIds: string[];
  onPick: (id: string) => void | Promise<void>;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const { t } = useI18n();
  const { data: facets } = useAdminSkillFacets();
  const parsed = useMemo(() => parseUnifiedSkillSearch(query), [query]);
  const { data, isLoading } = useTenantSkillCatalog({
    search: parsed.search,
    vertical: parsed.vertical,
    function: parsed.functionName,
    tags_all: parsed.tagsAll.length ? parsed.tagsAll : undefined,
    tags_none: parsed.tagsNone.length ? parsed.tagsNone : undefined,
    lifecycle: 'published',
    page_size: 50,
  });
  const list = useMemo(() => {
    const arr = !data
      ? []
      : Array.isArray(data)
        ? data
        : data.data ?? [];
    return arr.filter((s) => !excludeIds.includes(s.id));
  }, [data, excludeIds]);

  return (
    <div className="mt-3 border rounded-md p-3 bg-muted/30">
      <div className="flex items-center gap-2 mb-2">
        <UnifiedSkillSearchInput
          autoFocus
          placeholder={
            t('admin.skills.agentSkillsTab.searchFiltersPlaceholder') ||
            'Search + smart filters: vertical:, function:, tag:, -tag:'
          }
          value={query}
          onChange={setQuery}
          facets={{
            verticals: facets?.verticals ?? [],
            functions: facets?.functions ?? [],
            tags: facets?.tags ?? [],
          }}
          className="flex-1"
        />
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('admin.skills.agentSkillsTab.cancelButton') || 'Cancel'}
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      ) : list.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2 text-center">
          {t('admin.skills.agentSkillsTab.noMatchingSkills') ||
            'No matching skills.'}
        </p>
      ) : (
        <div className="space-y-1 max-h-64 overflow-auto">
          {list.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onPick(s.id)}
              className="w-full text-left flex items-center gap-2 px-2 py-2 rounded hover:bg-accent"
            >
              <Badge variant="outline" className="font-mono text-xs">
                {s.slug}
              </Badge>
              <span className="flex-1 truncate text-sm">{s.display_name}</span>
              <Badge variant="outline" className="text-xs">
                {s.vertical}
              </Badge>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default AgentSkillsTab;
